import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import axios from 'axios'
import { useApiCredentials } from '@/composables/useApiCredentials'
import useWrapper from '@/composables/useWrapper'

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface ScannerConfig {
    scanner: string
    params: Record<string, any>
}

export interface Guardrail {
    id: number
    app_id: number
    name: string
    description: string
    input_scanners: ScannerConfig[]
    output_scanners: ScannerConfig[]
    is_active: boolean
    fail_fast: boolean
    action: 'block' | 'log'
    created_by?: string | null
    created_at: string
    updated_at: string
}

export interface ScannerInfo {
    input_scanners: string[]
    output_scanners: string[]
}

export interface GuardrailScanResult {
    scanner: string
    is_valid: boolean
    risk_score: number
}

export interface GuardrailScanResponse {
    guardrail_id: number
    guardrail_name: string
    passed: boolean
    action: string
    results: GuardrailScanResult[]
    sanitized_text?: string | null
}

export interface CreateGuardrailRequest {
    name: string
    description?: string
    input_scanners?: ScannerConfig[]
    output_scanners?: ScannerConfig[]
    is_active?: boolean
    fail_fast?: boolean
    action?: 'block' | 'log'
}

export interface UpdateGuardrailRequest {
    name?: string
    description?: string
    input_scanners?: ScannerConfig[]
    output_scanners?: ScannerConfig[]
    is_active?: boolean
    fail_fast?: boolean
    action?: 'block' | 'log'
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useGuardrailStore = defineStore('guardrailStore', () => {
    const guardrails: Ref<Guardrail[]> = ref([])
    const scannerInfo: Ref<ScannerInfo | null> = ref(null)
    const initialized: Ref<boolean> = ref(false)
    const isLoading: Ref<boolean> = ref(false)

    const { rootGuid, rootAxiosBearerHeader, portalBaseUrl } = useApiCredentials()
    const { handleAxiosError, rootAuth } = useWrapper()

    const baseRoute = computed(() => `${portalBaseUrl}/${rootGuid.value}/guardrails`)

    // ── CRUD ──────────────────────────────────────────────────────────────

    const loadGuardrails = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            isLoading.value = true
            try {
                const { data } = await axios.get<Guardrail[]>(
                    baseRoute.value,
                    rootAxiosBearerHeader.value
                )
                guardrails.value = data
                initialized.value = true
            } finally {
                isLoading.value = false
            }
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    const createGuardrail = handleAxiosError(
        rootAuth(async (body: CreateGuardrailRequest): Promise<Guardrail> => {
            const { data } = await axios.post<Guardrail>(
                baseRoute.value,
                body,
                rootAxiosBearerHeader.value
            )
            guardrails.value.push(data)
            return data
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    const updateGuardrail = handleAxiosError(
        rootAuth(async (id: number, body: UpdateGuardrailRequest): Promise<Guardrail> => {
            const { data } = await axios.put<Guardrail>(
                `${baseRoute.value}/${id}`,
                body,
                rootAxiosBearerHeader.value
            )
            const idx = guardrails.value.findIndex(g => g.id === id)
            if (idx !== -1) guardrails.value[idx] = data
            return data
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    const deleteGuardrail = handleAxiosError(
        rootAuth(async (id: number): Promise<void> => {
            await axios.delete(
                `${baseRoute.value}/${id}`,
                rootAxiosBearerHeader.value
            )
            guardrails.value = guardrails.value.filter(g => g.id !== id)
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    // ── Scanner Discovery ─────────────────────────────────────────────────

    const loadScanners = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            const { data } = await axios.get<ScannerInfo>(
                `${baseRoute.value}/scanners`,
                rootAxiosBearerHeader.value
            )
            scannerInfo.value = data
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    // ── Scan Endpoints ────────────────────────────────────────────────────

    const scanInput = handleAxiosError(
        rootAuth(async (guardrailId: number, text: string): Promise<GuardrailScanResponse> => {
            const { data } = await axios.post<GuardrailScanResponse>(
                `${baseRoute.value}/scan/input`,
                { guardrail_id: guardrailId, text },
                rootAxiosBearerHeader.value
            )
            return data
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    const scanOutput = handleAxiosError(
        rootAuth(async (guardrailId: number, output: string, prompt?: string): Promise<GuardrailScanResponse> => {
            const { data } = await axios.post<GuardrailScanResponse>(
                `${baseRoute.value}/scan/output`,
                { guardrail_id: guardrailId, output, prompt: prompt || '' },
                rootAxiosBearerHeader.value
            )
            return data
        }, true),
        { errorMsg: 'guardrails.messages.error' }
    )

    // ── Helpers ───────────────────────────────────────────────────────────

    const activeGuardrails = computed(() =>
        guardrails.value.filter(g => g.is_active)
    )

    return {
        // State
        guardrails,
        scannerInfo,
        initialized,
        isLoading,
        activeGuardrails,
        // CRUD
        loadGuardrails,
        createGuardrail,
        updateGuardrail,
        deleteGuardrail,
        // Scanners
        loadScanners,
        scanInput,
        scanOutput,
    }
})
